<template>
    <component
        :is="tagName"
        :href="hrefFormatted"
        class="button"
        :class="classes"
        :data-test="dataTest"
        :data-testid="dataTestid"
        v-bind="attributes"
        ref="rootElement"
        @click="buttonClickHandler"
    >
        <span v-if="iconLeft" class="button__icon">
            <template v-if="size === 'sm'">
                <DsSystemIcon :name="getThinIconName(iconLeft)" :size="16" />
            </template>
            <template v-else-if="size === 'lg'">
                <DsSystemIcon :name="getRegularIconName(iconLeft)" :size="24" />
            </template>
            <template v-else>
                <DsSystemIcon :name="iconLeft" />
            </template>
        </span>
        <span v-if="!!slots.default" class="button__inner">
            <slot></slot>
        </span>
        <span v-if="iconRight" class="button__icon">
            <template v-if="size === 'sm'">
                <DsSystemIcon :name="getThinIconName(iconRight)" :size="16" />
            </template>
            <template v-else-if="size === 'lg'">
                <DsSystemIcon :name="getRegularIconName(iconRight)" :size="24" />
            </template>
            <template v-else>
                <DsSystemIcon :name="iconRight" />
            </template>
        </span>
        <slot v-if="!!slots.right" name="right"></slot>
    </component>
</template>

<script lang="ts" setup>
import { computed, ref, useSlots } from "vue";
import { MobileAppMethodName } from "@allwyn/mobile-apps/mobile-app-models";
import { DsButtonExpose, DsButtonRefType } from "../button/ds-button-reference";
import DsSystemIcon from "../icon/ds-system-icon.vue";
import { IconAssets } from "../icon/icon-assets";
import { SystemIcon } from "../icon/system-icon-types";
import { ButtonType } from "../types/button";
import { Size } from "../types/size";
import { Theme } from "../types/theme";

type ButtonAttributes = {
    target?: "_blank";
    rel?: string;
    disabled?: "";
    type?: "button" | "submit";
};

const slots = useSlots();

const rootElement = ref<DsButtonRefType | null>(null);

defineExpose<DsButtonExpose>({
    rootElement,
});

const props = withDefaults(
    defineProps<{
        tag?: "a" | "button" | "span" | "auto";
        href?: string;
        type?: ButtonType;
        theme?: Theme;
        size?: Size;
        isDisabled?: boolean;
        iconLeft?: SystemIcon;
        iconRight?: SystemIcon;
        openInNewTab?: boolean;
        isExternal?: boolean;
        isBlock?: boolean;
        dataTest?: string;
        dataTestid?: string;
        htmlType?: "button" | "submit";
        eventName?: MobileAppMethodName | Omit<string, MobileAppMethodName>;
        eventArgument?: string;
    }>(),
    {
        tag: "auto",
        type: "primary",
        theme: "light",
        size: "default",
        openInNewTab: false,
        isExternal: false,
        isBlock: false,
        htmlType: "button",
    }
);

const mobileAppIntegration: {
    isAppWebView: boolean;
    triggerMobileAppEvent: typeof import("@sazka/mobile-apps/mobile-app-integration").triggerMobileAppEvent;
} = {
    isAppWebView: false,
    triggerMobileAppEvent: () => undefined,
};

if (!IconAssets.isStorybook()) {
    import("@allwyn/mobile-apps/mobile-app-integration").then(({ triggerMobileAppEvent }) => {
        mobileAppIntegration["isAppWebView"] = environment.webView.isAppWebView;
        mobileAppIntegration["triggerMobileAppEvent"] = triggerMobileAppEvent;
    });
}

const hrefFormatted = computed(() => props.href?.replace("~", ""));

const tagName = computed(() => {
    if (props.tag === "auto") {
        return hrefFormatted.value ? "a" : "button";
    }
    return props.tag;
});

const classes = computed(() => [
    `button--${props.type}`,
    `button--${props.theme}`,
    props.size !== "default" ? `button--${props.size}` : "",
    props.isDisabled ? "button--disabled" : "",
    props.iconLeft || props.iconRight ? "button--icon" : "",
    props.isBlock ? "button--block" : "",
]);

const attributes = computed((): ButtonAttributes => {
    if (tagName.value === "button") {
        return {
            disabled: props.isDisabled ? "" : undefined,
            type: props.htmlType,
        };
    }

    const result: ButtonAttributes = {
        disabled: props.isDisabled ? "" : undefined,
    };

    if (props.openInNewTab || props.isExternal) {
        result.target = "_blank";
        result.rel = props.isExternal ? "noopener noreferrer" : "noopener";
    }

    return result;
});

const regexp = /.*-thin/gm;

const getThinIconName = (iconName: SystemIcon): SystemIcon => {
    return regexp.test(iconName) ? iconName : (`${iconName}-thin` as SystemIcon);
};

const getRegularIconName = (iconName: SystemIcon): SystemIcon => {
    return !regexp.test(iconName) ? iconName : (iconName.slice(0, -5) as SystemIcon);
};

const buttonClickHandler = (event: Event) => {
    if (IconAssets.isStorybook()) return;

    if (props.isDisabled) {
        event.preventDefault();
    } else if (props.eventName && mobileAppIntegration.isAppWebView) {
        event.preventDefault();
        mobileAppIntegration.triggerMobileAppEvent(props.eventName as string, props.eventArgument);
    }
};
</script>
